<template>
  <div class="settings-step">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>设定管理</span>
          <el-button type="primary" @click="extractSettings" :loading="extracting">
            <el-icon><MagicStick /></el-icon>
            AI提取设定
          </el-button>
        </div>
      </template>

      <el-empty v-if="!hasSettings" description="暂无设定数据">
        <p>点击"AI提取设定"按钮，从小说原文中提取角色、地点、道具等设定</p>
      </el-empty>

      <div v-else class="settings-content">
        <!-- 角色设定 -->
        <el-card class="setting-section" shadow="hover">
          <template #header>
            <div class="section-header">
              <span class="section-title">
                <el-icon><User /></el-icon>
                角色设定 ({{ settings.characters?.length || 0 }})
              </span>
              <el-button type="primary" size="small" @click="addCharacter">
                <el-icon><Plus /></el-icon>
                添加角色
              </el-button>
            </div>
          </template>
          
          <el-table :data="settings.characters" style="width: 100%" v-if="settings.characters?.length">
            <el-table-column type="index" width="50" />
            <el-table-column label="姓名" width="120">
              <template #default="{ row }">
                <span>{{ row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column label="身份" width="150">
              <template #default="{ row }">
                <span>{{ row.identity || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="外貌">
              <template #default="{ row }">
                <el-tooltip :content="row.appearance" placement="top" :show-after="500">
                  <span class="ellipsis">{{ row.appearance || '-' }}</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="性格" width="150">
              <template #default="{ row }">
                <span>{{ row.personality || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template #default="{ row, $index }">
                <el-button type="primary" size="small" @click="editCharacter(row, $index)">
                  <el-icon><Edit /></el-icon>
                  修改
                </el-button>
                <el-button type="danger" size="small" @click="removeCharacter($index)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <!-- 地点设定 -->
        <el-card class="setting-section" shadow="hover">
          <template #header>
            <div class="section-header">
              <span class="section-title">
                <el-icon><Location /></el-icon>
                地点设定 ({{ settings.locations?.length || 0 }})
              </span>
              <el-button type="primary" size="small" @click="addLocation">
                <el-icon><Plus /></el-icon>
                添加地点
              </el-button>
            </div>
          </template>
          
          <el-table :data="settings.locations" style="width: 100%" v-if="settings.locations?.length">
            <el-table-column type="index" width="50" />
            <el-table-column label="名称" width="150">
              <template #default="{ row }">
                <span>{{ row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column label="描述">
              <template #default="{ row }">
                <el-tooltip :content="row.description" placement="top" :show-after="500">
                  <span class="ellipsis">{{ row.description || '-' }}</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="重要性" width="100">
              <template #default="{ row }">
                <el-tag :type="getSignificanceType(row.significance)">
                  {{ getSignificanceLabel(row.significance) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template #default="{ row, $index }">
                <el-button type="primary" size="small" @click="editLocation(row, $index)">
                  <el-icon><Edit /></el-icon>
                  修改
                </el-button>
                <el-button type="danger" size="small" @click="removeLocation($index)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <!-- 道具设定 -->
        <el-card class="setting-section" shadow="hover">
          <template #header>
            <div class="section-header">
              <span class="section-title">
                <el-icon><Box /></el-icon>
                道具设定 ({{ settings.props?.length || 0 }})
              </span>
              <el-button type="primary" size="small" @click="addProp">
                <el-icon><Plus /></el-icon>
                添加道具
              </el-button>
            </div>
          </template>
          
          <el-table :data="settings.props" style="width: 100%" v-if="settings.props?.length">
            <el-table-column type="index" width="50" />
            <el-table-column label="名称" width="150">
              <template #default="{ row }">
                <span>{{ row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column label="描述">
              <template #default="{ row }">
                <el-tooltip :content="row.description" placement="top" :show-after="500">
                  <span class="ellipsis">{{ row.description || '-' }}</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="功能" width="150">
              <template #default="{ row }">
                <span>{{ row.function || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="所属" width="120">
              <template #default="{ row }">
                <span>{{ row.owner || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template #default="{ row, $index }">
                <el-button type="primary" size="small" @click="editProp(row, $index)">
                  <el-icon><Edit /></el-icon>
                  修改
                </el-button>
                <el-button type="danger" size="small" @click="removeProp($index)">
                  <el-icon><Delete /></el-icon>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <!-- 世界观设定 -->
        <el-card class="setting-section" shadow="hover" v-if="settings.world">
          <template #header>
            <div class="section-header">
              <span class="section-title">
                <el-icon><Collection /></el-icon>
                世界观设定
              </span>
              <el-button type="primary" size="small" @click="editWorld">
                <el-icon><Edit /></el-icon>
                修改
              </el-button>
            </div>
          </template>
          
          <el-descriptions :column="2" border>
            <el-descriptions-item label="世界观概述" :span="2">
              {{ settings.world?.setting || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="时代背景">
              {{ settings.world?.era || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="主要地点">
              {{ settings.world?.location || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="视觉风格" :span="2">
              {{ settings.world?.visualStyle || '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <div class="actions">
          <el-button type="success" @click="confirmSettings" size="large">
            <el-icon><Check /></el-icon>
            确认设定并继续
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 角色编辑对话框 -->
    <el-dialog
      v-model="characterDialog.visible"
      :title="characterDialog.isEdit ? '编辑角色' : '添加角色'"
      width="600px"
    >
      <el-form :model="characterDialog.form" label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="角色姓名" required>
              <el-input v-model="characterDialog.form.name" placeholder="输入角色姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="重要程度">
              <el-select v-model="characterDialog.form.importance" style="width: 100%">
                <el-option label="主角" value="main" />
                <el-option label="配角" value="secondary" />
                <el-option label="路人" value="minor" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="身份/职业">
          <el-input v-model="characterDialog.form.identity" placeholder="如：剑客、医生、学生..." />
        </el-form-item>
        <el-form-item label="外貌特征">
          <el-input 
            v-model="characterDialog.form.appearance" 
            type="textarea" 
            :rows="3"
            placeholder="描述角色的外貌特征，如身高、发型、服装等..."
          />
        </el-form-item>
        <el-form-item label="性格特点">
          <el-input 
            v-model="characterDialog.form.personality" 
            type="textarea" 
            :rows="2"
            placeholder="描述角色的性格，如开朗、沉稳、狡猾等..."
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="characterDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveCharacter">保存</el-button>
      </template>
    </el-dialog>

    <!-- 地点编辑对话框 -->
    <el-dialog
      v-model="locationDialog.visible"
      :title="locationDialog.isEdit ? '编辑地点' : '添加地点'"
      width="600px"
    >
      <el-form :model="locationDialog.form" label-position="top">
        <el-form-item label="地点名称" required>
          <el-input v-model="locationDialog.form.name" placeholder="输入地点名称" />
        </el-form-item>
        <el-form-item label="重要性">
          <el-select v-model="locationDialog.form.significance" style="width: 100%">
            <el-option label="关键场景" value="key" />
            <el-option label="过渡场景" value="transition" />
            <el-option label="背景" value="background" />
          </el-select>
        </el-form-item>
        <el-form-item label="环境描述">
          <el-input 
            v-model="locationDialog.form.description" 
            type="textarea" 
            :rows="4"
            placeholder="描述地点的环境、氛围、特征等..."
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="locationDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveLocation">保存</el-button>
      </template>
    </el-dialog>

    <!-- 道具编辑对话框 -->
    <el-dialog
      v-model="propDialog.visible"
      :title="propDialog.isEdit ? '编辑道具' : '添加道具'"
      width="600px"
    >
      <el-form :model="propDialog.form" label-position="top">
        <el-form-item label="道具名称" required>
          <el-input v-model="propDialog.form.name" placeholder="输入道具名称" />
        </el-form-item>
        <el-form-item label="外观描述">
          <el-input 
            v-model="propDialog.form.description" 
            type="textarea" 
            :rows="3"
            placeholder="描述道具的外观、材质、大小等..."
          />
        </el-form-item>
        <el-form-item label="功能/意义">
          <el-input 
            v-model="propDialog.form.function" 
            type="textarea" 
            :rows="2"
            placeholder="描述道具的功能或象征意义..."
          />
        </el-form-item>
        <el-form-item label="所属角色">
          <el-select v-model="propDialog.form.owner" placeholder="选择所属角色" style="width: 100%">
            <el-option 
              v-for="char in settings.characters" 
              :key="char.name" 
              :label="char.name" 
              :value="char.name" 
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="propDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveProp">保存</el-button>
      </template>
    </el-dialog>

    <!-- 世界观编辑对话框 -->
    <el-dialog
      v-model="worldDialog.visible"
      title="编辑世界观"
      width="600px"
    >
      <el-form :model="worldDialog.form" label-position="top">
        <el-form-item label="世界观概述">
          <el-input 
            v-model="worldDialog.form.setting" 
            type="textarea" 
            :rows="4"
            placeholder="描述故事的世界观背景..."
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="时代背景">
              <el-input v-model="worldDialog.form.era" placeholder="如：古代、现代、未来..." />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="主要地点">
              <el-input v-model="worldDialog.form.location" placeholder="故事主要发生的地点..." />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="视觉风格">
          <el-select v-model="worldDialog.form.visualStyle" placeholder="选择视觉风格" style="width: 100%">
            <el-option label="古风唯美" value="古风唯美" />
            <el-option label="现代都市" value="现代都市" />
            <el-option label="科幻未来" value="科幻未来" />
            <el-option label="奇幻魔法" value="奇幻魔法" />
            <el-option label="悬疑惊悚" value="悬疑惊悚" />
            <el-option label="温馨治愈" value="温馨治愈" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="worldDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveWorld">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Edit, Collection } from '@element-plus/icons-vue'
import { useProjectStore } from '../../stores/project'
import { extractSettings as extractSettingsAPI, saveSettings, getSettings, clearSettings } from '../../api/project'

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const projectStore = useProjectStore()
const settings = ref({
  characters: [],
  locations: [],
  props: [],
  world: {
    setting: '',
    era: '',
    location: '',
    visualStyle: ''
  }
})
const extracting = ref(false)
const saving = ref(false)

// 对话框状态
const characterDialog = reactive({
  visible: false,
  isEdit: false,
  index: -1,
  form: {
    name: '',
    appearance: '',
    personality: '',
    identity: '',
    importance: 'secondary'
  }
})

const locationDialog = reactive({
  visible: false,
  isEdit: false,
  index: -1,
  form: {
    name: '',
    description: '',
    significance: 'key'
  }
})

const propDialog = reactive({
  visible: false,
  isEdit: false,
  index: -1,
  form: {
    name: '',
    description: '',
    function: '',
    owner: ''
  }
})

const worldDialog = reactive({
  visible: false,
  form: {
    setting: '',
    era: '',
    location: '',
    visualStyle: ''
  }
})

// 计算属性
const hasSettings = computed(() => {
  return settings.value.characters?.length > 0 ||
         settings.value.locations?.length > 0 ||
         settings.value.props?.length > 0 ||
         settings.value.world?.setting
})

// 加载已有设定
onMounted(async () => {
  await loadSettings()
})

const loadSettings = async () => {
  try {
    const existingSettings = await getSettings(props.projectId)
    if (existingSettings && existingSettings.length > 0) {
      // 按类型分组
      const characters = []
      const locations = []
      const props = []
      let world = { setting: '', era: '', location: '', visualStyle: '' }

      existingSettings.forEach(setting => {
        const value = typeof setting.setting_value === 'string' 
          ? JSON.parse(setting.setting_value) 
          : setting.setting_value

        switch (setting.setting_type) {
          case 'character':
            characters.push(value)
            break
          case 'location':
            locations.push(value)
            break
          case 'prop':
            props.push(value)
            break
          case 'worldview':
            world = { ...world, ...value }
            break
        }
      })

      settings.value = {
        characters,
        locations,
        props,
        world
      }
    }
  } catch (error) {
    console.error('Load settings error:', error)
  }
}

// AI提取设定
const extractSettings = async () => {
  extracting.value = true
  try {
    const text = projectStore.project?.original_text
    if (!text) {
      ElMessage.warning('请先上传小说原文')
      return
    }

    const response = await extractSettingsAPI(props.projectId, text)

    if (response && (response.characters || response.locations || response.props)) {
      // 合并提取结果到现有设定
      if (response.characters?.length) {
        settings.value.characters = [...settings.value.characters, ...response.characters]
      }
      if (response.locations?.length) {
        settings.value.locations = [...settings.value.locations, ...response.locations]
      }
      if (response.props?.length) {
        settings.value.props = [...settings.value.props, ...response.props]
      }
      if (response.world) {
        settings.value.world = { ...settings.value.world, ...response.world }
      }
      ElMessage.success('设定提取成功')
    } else {
      ElMessage.warning('未能提取到设定信息')
    }
  } catch (error) {
    console.error('Extract settings error:', error)
    ElMessage.error('设定提取失败')
  } finally {
    extracting.value = false
  }
}

// 角色操作
const addCharacter = () => {
  characterDialog.isEdit = false
  characterDialog.index = -1
  characterDialog.form = {
    name: '',
    appearance: '',
    personality: '',
    identity: '',
    importance: 'secondary'
  }
  characterDialog.visible = true
}

const editCharacter = (row, index) => {
  characterDialog.isEdit = true
  characterDialog.index = index
  characterDialog.form = { ...row }
  characterDialog.visible = true
}

const saveCharacter = () => {
  if (!characterDialog.form.name) {
    ElMessage.warning('请输入角色姓名')
    return
  }
  
  if (characterDialog.isEdit) {
    settings.value.characters[characterDialog.index] = { ...characterDialog.form }
  } else {
    settings.value.characters.push({ ...characterDialog.form })
  }
  
  characterDialog.visible = false
  ElMessage.success(characterDialog.isEdit ? '角色修改成功' : '角色添加成功')
}

const removeCharacter = (index) => {
  settings.value.characters.splice(index, 1)
  ElMessage.success('角色已删除')
}

// 地点操作
const addLocation = () => {
  locationDialog.isEdit = false
  locationDialog.index = -1
  locationDialog.form = {
    name: '',
    description: '',
    significance: 'key'
  }
  locationDialog.visible = true
}

const editLocation = (row, index) => {
  locationDialog.isEdit = true
  locationDialog.index = index
  locationDialog.form = { ...row }
  locationDialog.visible = true
}

const saveLocation = () => {
  if (!locationDialog.form.name) {
    ElMessage.warning('请输入地点名称')
    return
  }
  
  if (locationDialog.isEdit) {
    settings.value.locations[locationDialog.index] = { ...locationDialog.form }
  } else {
    settings.value.locations.push({ ...locationDialog.form })
  }
  
  locationDialog.visible = false
  ElMessage.success(locationDialog.isEdit ? '地点修改成功' : '地点添加成功')
}

const removeLocation = (index) => {
  settings.value.locations.splice(index, 1)
  ElMessage.success('地点已删除')
}

// 道具操作
const addProp = () => {
  propDialog.isEdit = false
  propDialog.index = -1
  propDialog.form = {
    name: '',
    description: '',
    function: '',
    owner: ''
  }
  propDialog.visible = true
}

const editProp = (row, index) => {
  propDialog.isEdit = true
  propDialog.index = index
  propDialog.form = { ...row }
  propDialog.visible = true
}

const saveProp = () => {
  if (!propDialog.form.name) {
    ElMessage.warning('请输入道具名称')
    return
  }
  
  if (propDialog.isEdit) {
    settings.value.props[propDialog.index] = { ...propDialog.form }
  } else {
    settings.value.props.push({ ...propDialog.form })
  }
  
  propDialog.visible = false
  ElMessage.success(propDialog.isEdit ? '道具修改成功' : '道具添加成功')
}

const removeProp = (index) => {
  settings.value.props.splice(index, 1)
  ElMessage.success('道具已删除')
}

// 世界观操作
const editWorld = () => {
  worldDialog.form = { ...settings.value.world }
  worldDialog.visible = true
}

const saveWorld = () => {
  settings.value.world = { ...worldDialog.form }
  worldDialog.visible = false
  ElMessage.success('世界观修改成功')
}

// 辅助函数
const getSignificanceType = (significance) => {
  const typeMap = {
    'key': 'danger',
    'transition': 'warning',
    'background': 'info'
  }
  return typeMap[significance] || 'info'
}

const getSignificanceLabel = (significance) => {
  const labelMap = {
    'key': '关键',
    'transition': '过渡',
    'background': '背景'
  }
  return labelMap[significance] || '一般'
}

// 保存所有设定
const confirmSettings = async () => {
  saving.value = true
  try {
    // 先清除该项目的所有旧设定
    await clearSettings(props.projectId)

    // 将设定按类型分别保存
    const savePromises = []

    // 保存角色设定
    settings.value.characters.forEach((char) => {
      savePromises.push(saveSettings({
        project_id: props.projectId,
        setting_type: 'character',
        setting_name: char.name,
        setting_value: char
      }))
    })

    // 保存地点设定
    settings.value.locations.forEach((loc) => {
      savePromises.push(saveSettings({
        project_id: props.projectId,
        setting_type: 'location',
        setting_name: loc.name,
        setting_value: loc
      }))
    })

    // 保存道具设定
    settings.value.props.forEach((prop) => {
      savePromises.push(saveSettings({
        project_id: props.projectId,
        setting_type: 'prop',
        setting_name: prop.name,
        setting_value: prop
      }))
    })

    // 保存世界观设定
    if (settings.value.world?.setting) {
      savePromises.push(saveSettings({
        project_id: props.projectId,
        setting_type: 'worldview',
        setting_name: '世界观',
        setting_value: settings.value.world
      }))
    }

    await Promise.all(savePromises)

    // 更新项目状态
    await projectStore.updateProjectStatus('settings_extracted')

    // 重新加载设定以显示最新数据
    await loadSettings()

    ElMessage.success('设定保存成功')
    emit('step-complete')
  } catch (error) {
    console.error('Save settings error:', error)
    ElMessage.error('设定保存失败')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped lang="scss">
.settings-step {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.setting-section {
  margin-bottom: 20px;
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 16px;
  }
}

.ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
}

.actions {
  margin-top: 30px;
  text-align: center;
}
</style>
